import { useCallback } from "react";
import { permissions, MERGED_CSM_PERMISSIONS } from "./constants/Permissions";
import { decrypt } from "./others";
import { getRole } from "./auth";
import { useFeatureFlags } from "../featureFlags/context";
import { FLAGS } from "../featureFlags/registry";

const MERGE_FLAG = FLAGS.EPICENTER_MODERATOR_CSM_MERGE.name;

// SPRIN-3237 — "does this role carry Moderator's identity?", given a flag checker.
//
// Moderator always; cs_person only once the merge flag is ON. The checker is passed in rather
// than read from the imperative FeatureFlag singleton, because everything below is used on the
// RENDER path and has to re-render when the flag resolves — see the note on the hooks.
const isModeratorLike = (role, isFlagOn) =>
  role === "Moderator" || (role === "cs_person" && isFlagOn(MERGE_FLAG));

const checkPermission = (feature, role, isFlagOn) => {
  const allowed = isModeratorLike(role, isFlagOn)
    ? MERGED_CSM_PERMISSIONS
    : permissions[role] || [];
  return allowed.includes(feature);
};

/**
 * Every hook here goes through useFeatureFlags(), and that subscription is the point.
 *
 * The Statsig client initialises asynchronously, and until it is ready the provider answers every
 * gate with its registry default — false. So the first paint of any flag-dependent UI is the
 * flag-OFF version, and it must re-render once the real values land.
 *
 * Reading the imperative FeatureFlag.isFeatureActive instead does NOT do that: the provider swaps
 * it in through registerChecker, which mutates a module variable and re-renders nothing. That is
 * what left a merged CSM staring at a nav with no Courses or Users tab until an unrelated state
 * change happened to re-render it. Subscribing to the context makes the update arrive on its own.
 */
export const useHasPermission = () => {
  const isFlagOn = useFeatureFlags();
  const role = decrypt(localStorage.getItem("user"))?.role;

  // Stable identity per role and checker, so the returned function can sit in a
  // hook dependency array — it used to be a fresh closure every render, which
  // made every memo depending on it recompute unconditionally. The provider
  // memoises `isFeatureActive`, so this changes only when the flags resolve.
  return useCallback(
    (feature) => checkPermission(feature, role, isFlagOn),
    [role, isFlagOn],
  );
};

// For the role-string gates that no permission list covers — the payment Edit column, the inline
// class date/time inputs, and Moderator's limits inside user management.
export const useIsModeratorLikeRole = () => {
  const isFlagOn = useFeatureFlags();

  return (role) => isModeratorLike(role, isFlagOn);
};

// The mirror: a cs_person the merge has not yet freed from the hardcoded edit guards.
export const useIsRestrictedCsm = () => {
  const isFlagOn = useFeatureFlags();

  return getRole() === "cs_person" && !isFlagOn(MERGE_FLAG);
};
